// src/services/chat/messageActionsService.js

import { auth, db } from '../../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
  deleteDoc,
  writeBatch,
  arrayUnion,
  arrayRemove,
  FieldValue,
  query,
  orderBy,
  limit,
  onSnapshot,
} from 'firebase/firestore';
import { ChatMessage, MessageStatus, MessageType } from '../../models/chatModels/chatMessage';
import { firebaseMessageService } from './messageService';

class MessageActionService {
  // ── Singleton ──────────────────────────────────────────────────────────────

  constructor() {
    this._db = db;
    this._auth = auth;
  }

  static getInstance() {
    if (!MessageActionService._instance) {
      MessageActionService._instance = new MessageActionService();
    }
    return MessageActionService._instance;
  }

  get _uid() {
    return this._auth.currentUser?.uid || '';
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  _chatRef(conversationId) {
    return doc(this._db, 'chats', conversationId);
  }

  _msgRef(conversationId, messageId) {
    return doc(this._db, 'chats', conversationId, 'messages', messageId);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // REPLY
  // ─────────────────────────────────────────────────────────────────────────

  buildReplyMetadata(replyTo) {
    let type = 'text';
    let preview = replyTo.content || '';

    if (replyTo.messageType === MessageType.IMAGE) {
      type = 'image';
      preview = '📷 Photo';
    } else if (replyTo.messageType === MessageType.AUDIO) {
      type = 'audio';
      preview = '🎤 Voice message';
    } else if (replyTo.messageType === MessageType.VIDEO) {
      type = 'video';
      preview = '🎥 Video';
    } else if (replyTo.messageType === MessageType.FILE) {
      type = 'file';
      preview = `📎 ${replyTo.attachmentName || 'File'}`;
    } else {
      // Text message
      preview = replyTo.content || '';
      if (preview.length > 80) {
        preview = preview.substring(0, 80) + '…';
      }
    }

    return {
      replyToId: replyTo.id,
      replyToContent: preview,
      replyToSenderId: replyTo.senderId,
      replyToType: type,
      replyToAttachmentUrl: replyTo.attachmentUrl || null,
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // PIN / UNPIN
  // ─────────────────────────────────────────────────────────────────────────

  async pinMessage(conversationId, messageId) {
    try {
      const chatRef = this._chatRef(conversationId);
      const snap = await getDoc(chatRef);
      const data = snap.data() || {};
      const pinned = [...(data.pinnedMessageIds || [])];

      if (pinned.includes(messageId)) return; // already pinned

      if (pinned.length >= 3) pinned.shift(); // bump oldest
      pinned.push(messageId);

      const batch = writeBatch(this._db);
      batch.set(chatRef, { pinnedMessageIds: pinned }, { merge: true });
      batch.update(this._msgRef(conversationId, messageId), {
        pinnedAt: FieldValue.serverTimestamp(),
      });
      await batch.commit();
      console.log('Pinned message', messageId, 'in', conversationId);
    } catch (error) {
      console.error('pinMessage error:', error);
      throw error;
    }
  }

  async unpinMessage(conversationId, messageId) {
    try {
      const batch = writeBatch(this._db);
      batch.update(this._chatRef(conversationId), {
        pinnedMessageIds: arrayRemove([messageId]),
      });
      batch.update(this._msgRef(conversationId, messageId), {
        pinnedAt: FieldValue.delete(),
      });
      await batch.commit();
      console.log('Unpinned message', messageId);
    } catch (error) {
      console.error('unpinMessage error:', error);
      throw error;
    }
  }

  pinnedMessageIdsStream(conversationId) {
    return new Promise((resolve) => {
      const unsubscribe = onSnapshot(
        this._chatRef(conversationId),
        (snap) => {
          if (!snap.exists()) {
            resolve([]);
            return;
          }
          const data = snap.data();
          const pinnedIds = data.pinnedMessageIds || [];
          resolve(pinnedIds);
        },
        (error) => {
          console.error('pinnedMessageIdsStream error:', error);
          resolve([]);
        }
      );
      return unsubscribe;
    });
  }

  async fetchPinnedMessages(conversationId, pinnedIds) {
    if (!pinnedIds || pinnedIds.length === 0) return [];

    const results = [];
    for (const id of pinnedIds) {
      try {
        const snap = await getDoc(this._msgRef(conversationId, id));
        if (!snap.exists()) continue;

        const data = snap.data();

        // Parse timestamp
        let timestamp;
        const ts = data.ts;
        if (ts?.toDate) {
          timestamp = ts.toDate();
        } else if (typeof ts === 'string') {
          timestamp = new Date(ts);
        } else {
          timestamp = new Date();
        }

        const message = new ChatMessage({
          id: snap.id,
          conversationId: conversationId,
          senderId: data.from || '',
          receiverId: data.to || '',
          content: data.msg || '',
          timestamp: timestamp,
          status: data.status || 'sent',
          messageType: data.msgType || 'text',
          attachmentUrl: data.attachmentUrl || null,
          attachmentType: data.attachmentType || null,
          attachmentName: data.attachmentName || null,
          attachmentSize: data.attachmentSize || null,
          audioDuration: data.audioDuration || null,
          imageWidth: data.imageWidth || null,
          imageHeight: data.imageHeight || null,
          replyToMessageId: data.replyToMessageId || null,
          replyToContent: data.replyToContent || null,
          replyToSenderId: data.replyToSenderId || null,
          replyToType: data.replyToType || null,
          replyToAttachmentUrl: data.replyToAttachmentUrl || null,
          isForwarded: data.isForwarded || false,
          seenBy: data.seenBy || [],
          deletedFor: data.deletedFor || [],
          isDeleted: data.isDeleted || false,
          isDeletedForMe: false,
        });

        results.push(message);
      } catch (error) {
        console.error('fetchPinnedMessages error for', id, ':', error);
      }
    }
    return results;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // DELETE
  // ─────────────────────────────────────────────────────────────────────────

  async deleteForMe(conversationId, messageId) {
    try {
      await updateDoc(this._msgRef(conversationId, messageId), {
        deletedFor: arrayUnion([this._uid]),
      });
      console.log('Deleted for me:', messageId);
    } catch (error) {
      console.error('deleteForMe error:', error);
      throw error;
    }
  }

  async deleteForEveryone(conversationId, messageId) {
    try {
      await updateDoc(this._msgRef(conversationId, messageId), {
        deletedForAll: true,
        isDeleted: true,
        msg: '',
        attachmentUrl: FieldValue.delete(),
        localFilePath: FieldValue.delete(),
      });
      console.log('Deleted for everyone:', messageId);
    } catch (error) {
      console.error('deleteForEveryone error:', error);
      throw error;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FORWARD
  // ─────────────────────────────────────────────────────────────────────────

  async forwardMessages({
    messages,
    targetConversationId,
    targetReceiverId,
  }) {
    const msgService = firebaseMessageService;
    for (const msg of messages) {
      await msgService.sendMessage({
        conversationId: targetConversationId,
        receiverId: targetReceiverId,
        content: msg.content,
        messageType: msg.messageType,
        attachmentUrl: msg.attachmentUrl,
        attachmentType: msg.messageType === MessageType.IMAGE
          ? 'image/jpeg'
          : msg.messageType === MessageType.AUDIO
          ? 'audio/mp4'
          : msg.messageType === MessageType.VIDEO
          ? 'video/mp4'
          : null,
        audioDuration: msg.audioDuration,
        isForwarded: true,
      });
    }
    console.log('Forwarded', messages.length, 'message(s) to', targetConversationId);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // REACTION (optional feature)
  // ─────────────────────────────────────────────────────────────────────────

  async addReaction(conversationId, messageId, emoji) {
    try {
      const msgRef = this._msgRef(conversationId, messageId);
      await updateDoc(msgRef, {
        [`reactions.${emoji}`]: arrayUnion([this._uid]),
      });
    } catch (error) {
      console.error('addReaction error:', error);
      throw error;
    }
  }

  async removeReaction(conversationId, messageId, emoji) {
    try {
      const msgRef = this._msgRef(conversationId, messageId);
      await updateDoc(msgRef, {
        [`reactions.${emoji}`]: arrayRemove([this._uid]),
      });
    } catch (error) {
      console.error('removeReaction error:', error);
      throw error;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // DISPOSE
  // ─────────────────────────────────────────────────────────────────────────

  dispose() {
    // Clean up any subscriptions if needed
  }
}

// ── Singleton export ─────────────────────────────────────────────────────────

const messageActionsService = MessageActionService.getInstance();
export { messageActionsService };
export default messageActionsService;